import React from 'react';
import {
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Button,
  Stack,
  CircularProgress,
  Chip,
  DialogActions,
} from '@mui/material';
import EventsForm from '../pages/EventForm/EventFormMain';
import CloseIcon from "@mui/icons-material/Close";
import LockIcon from '@mui/icons-material/Lock';
import LaunchIcon from '@mui/icons-material/Launch';
import getUserRoleFlags from '../hooks/useGetUserRoleFlags';
import { useEventsApi } from '../hooks/useEventsApi';
import { config } from '../config';
import { resolveCounterpartGlobalIds, type CounterpartEventRef } from '../utils/counterpartEvent';


interface EventsFormDialogProps {
  open: boolean;
  onClose?: () => void;
  onSaved?: (p: { eventId?: number | null; eventCode?: string; approved?: boolean; created?: boolean }) => void;
  eventId: number | null;
  createNewEvent: boolean;
  /** Switch the dialog to another event (e.g. rising ↔ resolving counterpart) without closing. */
  onNavigateToEvent?: (globalEventId: number) => void;
  /**
   * When both global ids are already known (e.g. creator result page), skip list API counterpart
   * resolution and avoid duplicate GET /api/events/{id}/ while translation polling re-renders parents.
   */
  presetCounterparts?: { rising: number | null; resolving: number | null };
  forceReadOnly?: boolean;
  readOnlyHint?: string;
}

const EventsFormDialog: React.FC<EventsFormDialogProps> = ({
  open,
  onClose,
  onSaved,
  eventId,
  createNewEvent,
  onNavigateToEvent,
  presetCounterparts,
  forceReadOnly = false,
  readOnlyHint,
}) => {
  const onNavigateToEventRef = React.useRef(onNavigateToEvent);
  onNavigateToEventRef.current = onNavigateToEvent;
  const presetRisingId = presetCounterparts?.rising ?? null;
  const presetResolvingId = presetCounterparts?.resolving ?? null;
  const { isReader, isAdmin } = getUserRoleFlags();
  const effectiveReadOnly = isReader || forceReadOnly;
  const { callApi } = useEventsApi();
  const [counterparts, setCounterparts] = React.useState<{ rising: number | null; resolving: number | null } | null>(null);
  const [counterpartLoading, setCounterpartLoading] = React.useState(false);
  const [isDirty, setIsDirty] = React.useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = React.useState(false);
  const [pendingNavTarget, setPendingNavTarget] = React.useState<number | null>(null);
  const [pendingClose, setPendingClose] = React.useState(false);

  const handleSavedFromForm = (payload: { eventId?: number | null; eventCode?: string; approved?: boolean; created?: boolean }) => {
    setIsDirty(false);
    onSaved?.(payload); // Parent informieren
    onClose!();          // Dialog schließen
  };

  const requestClose = () => {
    if (isDirty) {
      setPendingClose(true);
      setPendingNavTarget(null);
      setDiscardConfirmOpen(true);
      return;
    }
    onClose?.();
  };

  const requestNavigate = (targetId: number) => {
    if (isDirty) {
      setPendingClose(false);
      setPendingNavTarget(targetId);
      setDiscardConfirmOpen(true);
      return;
    }
    onNavigateToEventRef.current?.(targetId);
  };

  const handleConfirmDiscard = () => {
    setDiscardConfirmOpen(false);
    setIsDirty(false);
    if (pendingClose) {
      setPendingClose(false);
      setPendingNavTarget(null);
      onClose?.();
      return;
    }
    if (pendingNavTarget != null) {
      const target = pendingNavTarget;
      setPendingNavTarget(null);
      onNavigateToEventRef.current?.(target);
    }
  };

  React.useEffect(() => {
    if (!open) {
      setIsDirty(false);
      setPendingClose(false);
      setPendingNavTarget(null);
      setDiscardConfirmOpen(false);
    }
  }, [open]);

  React.useEffect(() => {
    if (!open || !eventId || createNewEvent || !onNavigateToEventRef.current) {
      setCounterparts(null);
      setCounterpartLoading(false);
      return;
    }
    if (presetRisingId != null || presetResolvingId != null) {
      setCounterparts({ rising: presetRisingId, resolving: presetResolvingId });
      setCounterpartLoading(false);
      return;
    }
    let cancelled = false;
    setCounterpartLoading(true);
    (async () => {
      try {
        const full = (await callApi(`${config.apiUrl}/api/events/${eventId}/`, "GET")) as CounterpartEventRef;
        if (cancelled || !full) return;
        const cp = await resolveCounterpartGlobalIds(callApi, full);
        if (!cancelled) {
          setCounterparts(cp);
        }
      } catch {
        if (!cancelled) {
          setCounterparts(null);
        }
      } finally {
        if (!cancelled) {
          setCounterpartLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, eventId, createNewEvent, presetRisingId, presetResolvingId, callApi]);

  return (

    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") {
          requestClose();
          return;
        }
        onClose?.();
      }}
      maxWidth="md"
      fullWidth
    >

      <DialogTitle
        sx={{
          m: 0,
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Box display="flex" alignItems="center" gap={1}>
          {effectiveReadOnly && <LockIcon fontSize="small" />}
          {effectiveReadOnly ? (
            'Event Details'
          ) : (
            <Box display="flex" alignItems="baseline" gap={0.75}>
              <Box component="span">Edit Event</Box>
              {isAdmin && eventId != null && (
                <Box component="span" sx={{ typography: "caption", color: "text.secondary" }}>
                  ID: {eventId}
                </Box>
              )}
            </Box>
          )}
          {!effectiveReadOnly && isDirty && <Chip size="small" color="warning" label="Unsaved changes" />}
        </Box>

        <Stack direction="row" spacing={1} alignItems="center">
          {counterpartLoading && onNavigateToEvent && (
            <Button
              size="small"
              variant="outlined"
              disabled
              startIcon={<CircularProgress size={14} />}
            >
              Checking counterpart…
            </Button>
          )}
          {!counterpartLoading && onNavigateToEvent && counterparts?.rising != null && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<LaunchIcon fontSize="small" />}
              onClick={() => requestNavigate(counterparts.rising!)}
            >
              Open rising
            </Button>
          )}
          {!counterpartLoading && onNavigateToEvent && counterparts?.resolving != null && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<LaunchIcon fontSize="small" />}
              onClick={() => requestNavigate(counterparts.resolving!)}
            >
              Open resolving
            </Button>
          )}
          <IconButton
            aria-label="close"
            onClick={requestClose}
            sx={{
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>


      <DialogContent
        dividers
        sx={{
          overflowY: "auto",
          overflowX: "hidden",
          overflowAnchor: "none",
        }}
      >
        {forceReadOnly && readOnlyHint && (
          <Alert severity="info" sx={{ mb: 2 }}>
            {readOnlyHint}
          </Alert>
        )}
        {open ? (
          <EventsForm
            key={eventId ?? (createNewEvent ? "create" : "edit")}
            eventId={eventId}
            onSaved={handleSavedFromForm}
            createNewEvent={createNewEvent}
            configuratorMode={false}
            onDirtyChange={setIsDirty}
            forceReadOnly={forceReadOnly}
          />
        ) : null}
      </DialogContent>
      <Dialog open={discardConfirmOpen} onClose={() => setDiscardConfirmOpen(false)}>
        <DialogTitle>Discard unsaved changes?</DialogTitle>
        <DialogContent>
          You have unsaved changes. If you continue, your edits will be lost.
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDiscardConfirmOpen(false);
              setPendingClose(false);
              setPendingNavTarget(null);
            }}
          >
            Stay
          </Button>
          <Button color="warning" variant="contained" onClick={handleConfirmDiscard}>
            Discard changes
          </Button>
        </DialogActions>
      </Dialog>

      {/*<DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>*/}
    </Dialog>

  );
};

export default EventsFormDialog;
